import { useMemo } from 'react';

import { UseMutationOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { showError, showSuccess } from '@components/toaster';
import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';
import { useScopedTranslate } from '@locale/translate';
import { downloadFile } from '@utils/browser';

import { handleError } from 'api/query-client';

import {
  createList,
  CreateListData,
  createSyncList,
  deleteList,
  editList,
  getListTemplate,
  List,
  loadList,
  loadLists,
  patchSyncList,
  syncList,
  SyncListCreateData,
  SyncListType,
  syncToManualList,
} from './requests';

export const getListsQueryKey = (queryString?: string) => ['lists', queryString].filter(Boolean);

export const useInvalidateLists = () => {
  const queryClient = useQueryClient();
  const invalidateLists = () => queryClient.invalidateQueries(getListsQueryKey());

  return { invalidateLists };
};

export const useLists = (queryString?: string) => {
  const query = useQuery({
    queryKey: getListsQueryKey(queryString),
    queryFn: () => loadLists(queryString),
    keepPreviousData: true,
  });
  return query;
};

export const useList = (
  id: string,
  options?: { onSuccess?: (list: List) => void; staleTime?: number; cacheTime?: number }
) => {
  return useQuery({
    queryKey: ['lists', id],
    queryFn: () => loadList(id),
    enabled: Boolean(id),
    ...options,
  });
};

type ValidationErrorResponse = AxiosError<{ nonFieldErrors?: string[]; name?: string[] }>;

export const useCreateList = (
  options?: UseMutationOptions<any, ValidationErrorResponse, CreateListData>
) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateLists } = useInvalidateLists();
  return useMutation({
    ...options,
    mutationFn: (data) => createList(data),
    onSuccess: (...data) => {
      invalidateLists();
      options?.onSuccess?.(...data);
      showSuccess(t('listCreated'));
    },
    onError: (error: ValidationErrorResponse, variables: CreateListData, context: unknown) => {
      handleError(() => options?.onError?.(error, variables, context));
    },
  });
};

export const useEditList = (
  options?: UseMutationOptions<any, ValidationErrorResponse, CreateListData & { id: string }>
) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateLists } = useInvalidateLists();
  return useMutation({
    ...options,
    mutationFn: ({ id, ...data }) => editList(id, data),
    onSuccess: (...data) => {
      invalidateLists();
      options?.onSuccess?.(...data);
      showSuccess(t('listUpdated'));
    },
    onError: (
      error: ValidationErrorResponse,
      variables: CreateListData & { id: string },
      context: unknown
    ) => {
      handleError(() => options?.onError?.(error, variables, context));
    },
  });
};

export const useDeleteList = (options?: UseMutationOptions<any, any, string>) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateLists } = useInvalidateLists();
  return useMutation({
    ...options,
    mutationFn: (id) => deleteList(id),
    onSuccess: (...data) => {
      invalidateLists();
      options?.onSuccess?.(...data);
      showSuccess(t('listDeleted'));
    },
  });
};

export const useFormBuilderLists = () => {
  const listsQuery = useLists('?include_hidden=1');

  const data = useMemo(() => {
    return (listsQuery.data ?? []).map((list) => ({
      value: list.shortId,
      label: list.name,
      options: list.options,
      isHidden: list.isHidden,
    }));
  }, [listsQuery.data]);

  return { data, isLoading: listsQuery.isLoading };
};

export const useListTemplate = () => {
  return useMutation({
    mutationFn: () => getListTemplate(),
    onSuccess: (response) => {
      downloadFile(response, 'lists-template.csv');
    },
  });
};

export const useCreateSyncList = () => {
  const { invalidateLists } = useInvalidateLists();
  return useMutation({
    mutationFn: (data: SyncListCreateData) => createSyncList(data),
    onSuccess: () => invalidateLists(),
  });
};

export const usePatchSyncList = () => {
  const { invalidateLists } = useInvalidateLists();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<SyncListType> }) =>
      patchSyncList(id, data),
    onSuccess: () => invalidateLists(),
  });
};

export const useSyncList = () => {
  const { t } = useScopedTranslate(locale);
  const { invalidateLists } = useInvalidateLists();
  return useMutation({
    mutationFn: (id: string) => syncList(id),
    onSuccess: () => {
      showSuccess(t('resyncSuccess'));
      invalidateLists();
    },
    onError: () => {
      showError(t('resyncError'));
    },
  });
};

export const useSyncToManualList = () => {
  const { invalidateLists } = useInvalidateLists();
  return useMutation({
    mutationFn: (id: string) => syncToManualList(id),
    onSuccess: () => invalidateLists(),
  });
};

export const locale = {
  en: {
    listCreated: 'List created',
    listUpdated: 'List Updated',
    listDeleted: 'List Deleted',
    resyncSuccess: 'List re-synced successfully',
    resyncError: 'Error re-syncing list',
  },
};
